import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { pdfjs } from 'react-pdf';
import UploadDocumentsAssetStyle from '../../../../Components/UploadDocuments/UploadDocumentsAssetStyle';
import { BombiixModal } from '../../../../Components/BombiixModal';
import Loader from '../../../../Components/Utility/Loader/Loader';
import { formatFileSize } from '../../../../Components/helper';
import { validateFileUpload } from '../utils/validation';
import { getSupportingDocPdfPageCount } from '../../../../services/developmentOrder.service';
import { toasterMsg } from '../../../../Components/Toaster/Toaster';

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

/**
 * Maximum number of PDF pages that become Design Detail canvases when "Add to
 * Design detail" is checked. Beyond this the user is asked to confirm; the backend
 * enforces the same cap authoritatively.
 */
const MAX_DESIGN_PDF_PAGES = 10;

/**
 * Counts the pages of a picked PDF File in the browser via pdf.js, without
 * rendering it.
 * @param {File} file - The picked PDF file.
 * @returns {Promise<number>} Page count (0 when it cannot be read).
 */
async function getPdfPageCount(file) {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
    return pdf.numPages;
  } catch {
    return 0;
  }
}

/**
 * Counts the pages of a PDF already stored in S3 (a picked asset or an existing
 * doc) by asking the backend, which reads the count server-side with pdfinfo.
 * Avoids downloading the whole file to the browser just to read numPages.
 * @param {string} devOrderId - Dev order the request is scoped to.
 * @param {string} objectKey - S3 key of the stored PDF.
 * @returns {Promise<number>} Page count (0 when it cannot be read).
 */
async function getPdfPageCountFromKey(devOrderId, objectKey) {
  try {
    const res = await getSupportingDocPdfPageCount(devOrderId, String(objectKey));
    if (res?.code !== 200) {
      return 0;
    }
    return res?.data?.pageCount || 0;
  } catch {
    return 0;
  }
}

const DESIGN_SUPPORTING_DOC_MODULE = 'designSupportingDoc';

/**
 * Asset-style upload modal shared by every supporting-document flow.
 * Design Detail uses it with its default base64 payload; other modules (BOM supporting
 * documents) pass `buildPayload` to shape their own request body, `children` to render
 * module-specific fields below the uploader, `bottomBarContent` for fields that belong on the
 * document-title row itself, and `fetchDocuments` to scope the library picker.
 */
function UploadSupportingDocModal({
  showUpload,
  acceptedFileExtenstion,
  devOrderId,
  submitData,
  editId,
  editData,
  modalLoading,
  module = DESIGN_SUPPORTING_DOC_MODULE,
  title,
  className,
  fetchDocuments,
  validateFile,
  buildPayload,
  bottomBarContent,
  children,
}) {
  const { t } = useTranslation(['common', 'assetLibraryAssets']);
  const docRef = useRef();
  const isDesignModule = module === DESIGN_SUPPORTING_DOC_MODULE;
  const fileValidator = validateFile || (isDesignModule ? (file) => validateFileUpload(file, t) : undefined);
  const [pageLimitPrompt, setPageLimitPrompt] = useState({ show: false, payload: null });
  const [isCheckingPages, setIsCheckingPages] = useState(false);

  /**
   * Reads the uploader selection and hands the caller either its own payload (when `buildPayload`
   * is supplied) or the Design Detail base64 payload. A builder returning a falsy value aborts the
   * submit so module-specific validation can keep the modal open. For a PDF that the user asked to
   * add to design detail, a page-count confirmation is shown when it exceeds the design-page cap.
   */
  const handleSubmit = async () => {
    const selected = docRef.current?.selectedDocument();
    if (!selected) return;

    if (buildPayload) {
      const payload = await buildPayload(selected);
      if (payload) {
        submitData(payload);
      }
      return;
    }

    const dataObject = {
      documentName: selected.fileName.replace(/[?+#]/g, '_'),
      expiredOn: selected.expairyDueDate,
      addToDesign: selected.addToDesign,
    };

    if (selected.document) {
      dataObject.documentSize = selected.document.size ? formatFileSize(selected.document.size) : '0KB';
      dataObject.file = selected.document;
      dataObject.originalFileExtension = selected.fileExtn;
      dataObject.isAddToAssetLibrary = selected.addToAssetLibrary === true;
    } else if (selected.existingAsset) {
      dataObject.documentSize = formatFileSize(selected.existingAsset.size || 0);
      dataObject.existingAssetId = selected.existingAsset.id;
      dataObject.originalFileExtension = selected.fileExtn;
    }

    if (editId) {
      dataObject.editId = editId;
    }

    const alreadyAddedToDesign = Boolean(editId) && editData?.addedToDesign === true;
    const currentExtension = (selected.fileExtn || editData?.fileExtension || '').toLowerCase();
    const isDesignPdf = selected.addToDesign && !alreadyAddedToDesign && currentExtension === 'pdf';
    if (isDesignPdf) {
      let pageCount = 0;
      setIsCheckingPages(true);
      try {
        if (selected.document) {
          pageCount = await getPdfPageCount(selected.document);
        } else if (selected.existingAsset?.key) {
          pageCount = await getPdfPageCountFromKey(devOrderId, selected.existingAsset.key);
        } else if (editId && editData?.document) {
          pageCount = await getPdfPageCountFromKey(devOrderId, editData.document);
        }
      } catch {
        pageCount = 0;
        toasterMsg(t('common:SOMETHING_WENT_WRONG'), 'danger');
      } finally {
        setIsCheckingPages(false);
      }
      if (pageCount > MAX_DESIGN_PDF_PAGES) {
        setPageLimitPrompt({ show: true, payload: dataObject });
        return;
      }
    }

    submitData(dataObject);
  };

  /** Yes — create the (≤10) design canvases and upload. */
  const confirmAddWithinLimit = () => {
    const { payload } = pageLimitPrompt;
    setPageLimitPrompt({ show: false, payload: null });
    if (payload) {
      submitData(payload);
    }
  };

  /** No — upload the file only, without adding it to design detail. */
  const declineAddToDesign = () => {
    const { payload } = pageLimitPrompt;
    setPageLimitPrompt({ show: false, payload: null });
    if (payload) {
      submitData({ ...payload, addToDesign: false });
    }
  };

  /** Close (X) — abort: dismiss the prompt and do not upload at all. */
  const cancelPageLimitPrompt = () => {
    setPageLimitPrompt({ show: false, payload: null });
  };

  return (
    <>
      <BombiixModal
        show={showUpload}
        onClose={() => submitData('close')}
        onSave={handleSubmit}
        saveLabel={t('COMMON_LABEL.SAVE')}
        saveLoading={modalLoading || isCheckingPages}
        title={title || t('TAKING_DATES.UPLOAD_DOCUMENTS')}
        className={className || 'asset-style-modal-dialog'}
      >
        {modalLoading ? (
          <Loader />
        ) : (
          <>
            <UploadDocumentsAssetStyle
              ref={docRef}
              module={module}
              productId={devOrderId}
              showAddToAssetLibrary
              fetchDocuments={fetchDocuments}
              accept={acceptedFileExtenstion}
              editId={editId}
              editData={editData}
              validateFile={fileValidator}
              bottomBarContent={bottomBarContent}
              onSelectedGradingGroupChange={() => {}}
              setShowGradingGroupError={() => {}}
            />
            {children}
          </>
        )}
      </BombiixModal>
      {pageLimitPrompt.show && (
        <BombiixModal
          show={pageLimitPrompt.show}
          title={t('TAKING_DATES.ADD_DESIGN_DETAILS')}
          onClose={cancelPageLimitPrompt}
          hideSave
          size="md"
          footer={
            <>
              <button type="button" className="bombiix-btn btn-red" onClick={declineAddToDesign}>
                {t('CONFIRMATION_POPUP.NO_BUTTON')}
              </button>
              <button type="button" className="bombiix-btn btn-blue ml-2" onClick={confirmAddWithinLimit}>
                {t('CONFIRMATION_POPUP.YES_BUTTON')}
              </button>
            </>
          }
        >
          <p className="text-center mb-0">{t('modules/design-detail:PDF_PAGE_LIMIT_CONFIRM', { limit: MAX_DESIGN_PDF_PAGES })}</p>
        </BombiixModal>
      )}
    </>
  );
}

export default UploadSupportingDocModal;
